// src/utils/coleta.js

/**
 * Regras de apresentação das páginas de coleta (leite, colostro, água). Puras — a
 * tela só monta. O que é regra de negócio (tendência, contagem com diluição) vem
 * pronto do backend (/coletas).
 */

/**
 * Textos e padrões de cada página. A análise de cada uma é configurada pelo ADMIN.
 * `icone` é o nome no lucide-react. `usaDiluicao`: leite e colostro não são diluídos —
 * esconder o ajuste evita que alguém mude a contagem exibida por engano.
 * As linhas de referência do gráfico (legislação e metas) vêm do servidor
 * (GET /paginas-coleta/{pagina}/referencias), a mesma fonte das regras do assistente.
 * `cor`: identifica a página nas listas que juntam as três (painel da propriedade).
 * `duracaoEstimadaHoras`: faixa típica do ensaio, para a barra de progresso da amostra em
 * andamento (o fim é o crescimento, não um relógio — por isso é estimativa).
 */
export const PAGINAS_COLETA = Object.freeze({
    LEITE: {
        titulo: 'Qualidade do leite',
        nomeCurto: 'leite',
        rota: 'leite',
        subtitulo: 'CBT na rotina. Compare coletas equivalentes e acompanhe a evolução.',
        prefixoCodigo: 'LEI',
        cor: '#2F80ED',
        usaDiluicao: false,
        duracaoEstimadaHoras: [8, 24],
        icone: 'Milk',
        alvoPadrao: 'mesofilostotais',
        unidadePadrao: 'UFC',
    },
    COLOSTRO: {
        titulo: 'Qualidade do colostro',
        nomeCurto: 'colostro',
        rota: 'colostro',
        subtitulo: 'CBT do colostro por coleta, com a evolução no período.',
        prefixoCodigo: 'COL',
        cor: '#E0A43A',
        usaDiluicao: false,
        duracaoEstimadaHoras: [8, 24],
        icone: 'Milk',
        alvoPadrao: 'mesofilostotais',
        unidadePadrao: 'UFC',
    },
    AGUA: {
        titulo: 'Água e higiene',
        nomeCurto: 'água',
        rota: 'agua',
        subtitulo: 'Acompanhe a água usada na ordenha, na limpeza e no manejo.',
        // Água é presença/ausência (Portaria 888, em 100 mL): tabela de detectado / não
        // detectado por alvo, sem gráfico de contagem, com as verificações da página.
        qualitativo: true,
        alvosQualitativos: [
            { alvo: 'ecoli', rotulo: 'E. coli' },
            { alvo: 'colitotais', rotulo: 'Coliformes totais' },
        ],
        prefixoCodigo: 'AGU',
        cor: '#2BA59B',
        usaDiluicao: true,
        icone: 'Droplets',
        alvoPadrao: 'colitotais',
        unidadePadrao: 'NMP',
    },
});

/** Alvos do modelo de inferência (nomes como o modelo os chama). */
export const ALVOS = Object.freeze([
    { valor: 'mesofilostotais', rotulo: 'Mesófilos totais (CBT)', curto: 'CBT' },
    { valor: 'colitotais', rotulo: 'Coliformes totais', curto: 'Coliformes totais' },
    { valor: 'ecoli', rotulo: 'Escherichia coli', curto: 'E. coli' },
]);

export const rotuloAlvo = (alvo) => ALVOS.find((a) => a.valor === alvo)?.curto || alvo || '';

export const TURNOS = Object.freeze([
    { valor: 'MANHA', rotulo: 'Manhã' },
    { valor: 'TARDE', rotulo: 'Tarde' },
    { valor: 'NOITE', rotulo: 'Noite' },
]);

export const rotuloTurno = (turno) => TURNOS.find((t) => t.valor === turno)?.rotulo || '';

/**
 * Turno sugerido pela hora da coleta — faixas fixas: manhã até 11:59, tarde até
 * 17:59, noite o resto. O usuário pode corrigir no formulário.
 */
export const turnoPelaHora = (data) => {
    const hora = data instanceof Date ? data.getHours() : NaN;
    if (!Number.isFinite(hora)) return '';
    if (hora < 12) return 'MANHA';
    if (hora < 18) return 'TARDE';
    return 'NOITE';
};

/** Períodos do filtro, em dias. */
export const PERIODOS = Object.freeze([7, 14, 30, 90]);

/** Intervalo [de, ate] em ISO para "últimos N dias" a partir de `agora`. */
export const periodoEmDatas = (dias, agora = new Date()) => {
    const de = new Date(agora);
    de.setDate(de.getDate() - dias);
    de.setHours(0, 0, 0, 0);
    return { de: de.toISOString(), ate: agora.toISOString() };
};

/** Contagem em escala natural a partir do log10 (já com diluição, vindo do backend). */
export const valorContagem = (log10) => (Number.isFinite(log10) ? 10 ** log10 : null);

const INTEIRO = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

export const formatarContagem = (log10) => {
    const valor = valorContagem(log10);
    return valor === null ? '—' : INTEIRO.format(valor);
};

const DUAS_CASAS = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A contagem também em log10, como o laboratório compara: "log₁₀ 5,96"; "" sem valor. */
export const formatarLog10 = (log10) => (Number.isFinite(log10) ? `log₁₀ ${DUAS_CASAS.format(log10)}` : '');

/** Eixo do gráfico: 900.000 → "900 mil", 1.200.000 → "1,2 mi". */
export const formatarContagemCompacta = (valor) =>
    Number.isFinite(valor)
        ? new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(valor)
        : '';

/** "+14,8%" / "-9,3%" / "0%". */
export const formatarVariacao = (pct) => {
    if (!Number.isFinite(pct)) return '';
    const texto = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(Math.abs(pct));
    if (Math.abs(pct) < 0.05) return '0%';
    return `${pct > 0 ? '+' : '-'}${texto}%`;
};

/**
 * Unidade completa para exibir ("UFC/mL"): o protocolo traz a unidade inteira nos
 * `calibrationSets`; a página guarda só "UFC"/"NMP".
 */
export const unidadeExibida = (protocolo, unidade) => {
    const prefixo = String(unidade || '').toUpperCase();
    const completa = (protocolo?.calibrationSets || [])
        .map((cs) => cs?.unit)
        .find((u) => typeof u === 'string' && u.toUpperCase().startsWith(prefixo));
    return completa || unidade || '';
};

/** Unidades que o modelo devolve (sufixo das chaves `predict_<alvo>_<ufc|nmp>`). */
const UNIDADES_MODELO = ['UFC', 'NMP'];

/**
 * Unidades da página a partir do protocolo: as que os `calibrationSets` medem e o
 * modelo entende. `valor` é o que se grava (UFC/NMP); `rotulo`, a unidade completa
 * do protocolo (UFC/mL). Protocolo sem `calibrationSets`: as duas do modelo.
 */
export const unidadesDoProtocolo = (protocolo) => {
    const opcoes = [];
    (protocolo?.calibrationSets || []).forEach((cs) => {
        const unit = String(cs?.unit || '');
        const valor = UNIDADES_MODELO.find((u) => unit.toUpperCase().startsWith(u));
        if (valor && !opcoes.some((o) => o.valor === valor)) opcoes.push({ valor, rotulo: unit });
    });
    return opcoes.length > 0 ? opcoes : UNIDADES_MODELO.map((u) => ({ valor: u, rotulo: u }));
};

/** Análises de todos os protocolos da conta, para escolher a da página. */
export const analisesDosProtocolos = (protocolos) =>
    (protocolos || []).flatMap((p) =>
        (p?.analysis || [])
            .filter((a) => a?.id)
            .map((a) => ({
                protocoloId: p.id,
                protocoloNome: p.name || p.id,
                analiseId: a.id,
                analiseNome: a.name || a.id,
            }))
    );

/** Situação da amostra na tela. */
export const situacaoColeta = (status) => {
    switch (status) {
        case 'PENDENTE':
            return { chave: 'aguardando', rotulo: 'Aguardando tubo', cor: 'warning' };
        case 'EM_PROCESSAMENTO':
            return { chave: 'andamento', rotulo: 'Em andamento', cor: 'info' };
        case 'FINALIZADA':
        case 'ENCERRADA':
        case 'ENVIADO':
            return { chave: 'concluida', rotulo: 'Concluída', cor: 'success' };
        default:
            return { chave: 'outro', rotulo: status || '—', cor: 'default' };
    }
};

const dois = (n) => String(n).padStart(2, '0');

/** Código sugerido no formulário: LEI-261001-0530 (o backend garante unicidade). */
export const codigoSugerido = (prefixo, data = new Date()) =>
    `${prefixo}-${String(data.getFullYear()).slice(2)}${dois(data.getMonth() + 1)}${dois(data.getDate())}` +
    `-${dois(data.getHours())}${dois(data.getMinutes())}`;

/** Valor de um <input type="datetime-local"> (hora local, sem fuso). */
export const paraInputDataHora = (data) =>
    `${data.getFullYear()}-${dois(data.getMonth() + 1)}-${dois(data.getDate())}T${dois(data.getHours())}:${dois(data.getMinutes())}`;

/** Inverso: texto do datetime-local → Date na hora local; `null` se inválido. */
export const deInputDataHora = (texto) => {
    const data = new Date(texto);
    return texto && !Number.isNaN(data.getTime()) ? data : null;
};

/**
 * Data de início/fim do experimento: o back-go grava epoch (segundos ou ms) em
 * texto ou ISO, conforme o caminho que criou o experimento.
 */
export const dataDoExperimento = (valor) => {
    if (valor === null || valor === undefined || valor === '') return null;
    const texto = String(valor).trim();
    if (/^\d+$/.test(texto)) {
        const n = Number(texto);
        return new Date(texto.length <= 10 ? n * 1000 : n);
    }
    const data = new Date(texto);
    return Number.isNaN(data.getTime()) ? null : data;
};

/** "02/10/2026 às 11:46"; "" sem data. */
export const formatarDataHora = (data) => {
    if (!(data instanceof Date) || Number.isNaN(data.getTime())) return '';
    return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${data.getFullYear()} às ${dois(data.getHours())}:${dois(data.getMinutes())}`;
};

/** Diluição do experimento (expoente de 10⁻ⁿ) para leitura. */
export const rotuloDiluicao = (n) => {
    if (n === null || n === undefined || n === '') return 'Não informada';
    const valor = Number(n);
    if (!Number.isFinite(valor)) return 'Não informada';
    return valor === 0 ? 'Sem diluição' : `10⁻${valor}`;
};

/** "3h 11min" desde `inicio` até `agora`. */
export const tempoDecorrido = (inicio, agora = new Date()) => {
    if (!(inicio instanceof Date)) return '';
    const minutos = Math.max(0, Math.floor((agora - inicio) / 60000));
    const horas = Math.floor(minutos / 60);
    return horas > 0 ? `${horas}h ${dois(minutos % 60)}min` : `${minutos}min`;
};

/**
 * "Uma ordenha": mesmo dia (local) + turno. Agrupa os pontos da série para comparar a
 * mesma ordenha em pontos de coleta diferentes. Mais recente primeiro.
 *
 * A origem NÃO entra na chave: na prática cada ponto ganha uma origem própria
 * ("Linha de ordenha 01" na saída, "Tanque 01" na entrada do tanque) e a mesma ordenha
 * se partia em grupos de um ponto só. As origens ficam listadas no grupo (`origens`).
 */
export const ordenhasDaSerie = (serie) => {
    const grupos = new Map();
    (serie || []).forEach((s) => {
        const data = new Date(s.dataHora);
        if (Number.isNaN(data.getTime())) return;
        const dia = `${data.getFullYear()}-${dois(data.getMonth() + 1)}-${dois(data.getDate())}`;
        const chave = `${dia}|${s.turno || ''}`;
        if (!grupos.has(chave)) {
            grupos.set(chave, { chave, data, turno: s.turno || null, origens: [], pontos: [] });
        }
        const g = grupos.get(chave);
        g.pontos.push(s);
        if (data > g.data) g.data = data;
        const origem = (s.origem || '').trim();
        if (origem && !g.origens.some((o) => o.toLowerCase() === origem.toLowerCase())) g.origens.push(origem);
    });
    return [...grupos.values()].sort((a, b) => b.data - a.data);
};

/** "29/09 · Manhã · Linha de ordenha 01 / Tanque 01" */
export const rotuloOrdenha = (o) =>
    [`${dois(o.data.getDate())}/${dois(o.data.getMonth() + 1)}`, rotuloTurno(o.turno), (o.origens || []).join(' / ')]
        .filter(Boolean)
        .join(' · ');

const DIA_MS = 24 * 60 * 60 * 1000;

/** Marcas diárias (meia-noite local) entre `de` e `ate`, espaçadas para caber ~`max`. */
export const marcasDiarias = (de, ate, max = 14) => {
    const inicio = new Date(de);
    const fim = new Date(ate);
    if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime()) || inicio > fim) return [];
    inicio.setHours(0, 0, 0, 0);
    const dias = Math.floor((fim - inicio) / DIA_MS) + 1;
    const passo = Math.max(1, Math.ceil(dias / max));
    const marcas = [];
    for (let i = 0; i < dias; i += passo) {
        const d = new Date(inicio);
        d.setDate(inicio.getDate() + i);
        marcas.push(d.getTime());
    }
    return marcas;
};
